import { type FC, type ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import DefinitionTooltip, { type DefinitionTooltipInfo } from "../DefinitionTooltip";
import { formatDashboardValueString } from "src/utils/formatDashboardValue";
import { isolateNumericRuns } from "src/utils/numberFormat";
import { useLanguage } from "src/hooks/useLanguage";

interface DashboardMetricCardProps {
  title: string;
  value: string;
  comparisonLabel: string;
  change?: number;
  icon: ReactNode;
  definition: DefinitionTooltipInfo;
}

const DashboardMetricCard: FC<DashboardMetricCardProps> = ({
  title,
  value,
  comparisonLabel,
  change,
  icon,
  definition,
}) => {
  const isPositive = (change ?? 0) >= 0;
  const { language } = useLanguage();
  const displayComparisonLabel = isolateNumericRuns(comparisonLabel, language);

  return (
    <article className="organic-content-card">
      <div className="organic-content-card__header">
        <div>
          <div className="organic-content-card__title-row">
            <h3>{title}</h3>
            <DefinitionTooltip label={title} info={definition} />
          </div>
          <div className="organic-content-card__value">{formatDashboardValueString(value)}</div>
        </div>
        <div className="organic-content-card__icon" aria-hidden>
          {icon}
        </div>
      </div>

      <div className="organic-content-card__footer">
        <div className="organic-content-card__period">
          {typeof change === "number" ? (
            <span
              className={`organic-content-card__change organic-content-card__change--${
                isPositive ? "positive" : "negative"
              }`}
            >
              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {change.toFixed(2)}%
            </span>
          ) : null}
          <span>{displayComparisonLabel}</span>
        </div>
      </div>
    </article>
  );
};

export default DashboardMetricCard;
